import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Chip, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { hexToGrayscale } from "pdf-generator";
import React from "react";
import { useDispatch } from "react-redux";
import { ColorPalette, HighlightSelectBox } from "../../../components";
import { ReactSelectOption, SelectBoxOption } from "../../../interfaces";
import { useCreateTagMutation, useGetTagGroupsDDQuery, useUpdateTagMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeDialog } from "../../../redux/slices/dialog";
import { getRandomColor } from "../../../utils";

export const AddEditTags: React.FC<{ children?: JSX.Element, isEdit: boolean, data: any, refetch: any, selectedTagGroup?: SelectBoxOption, totalCount?: number, onDialogClose?: (newTag?: any) => void }> = (props) => {

	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const searchparams = new URLSearchParams(window.location.search);
	const [createTag, createTagData] = useCreateTagMutation();
	const [updateTag, updateTagData] = useUpdateTagMutation();
	const { data: groups, isFetching, isError, isSuccess } = useGetTagGroupsDDQuery(null);

	const defaultColor = props.isEdit ? "#FFFFFF" : getRandomColor();

	const [state, setState] = React.useState<{ group: SelectBoxOption, name: string, description: string, color: string }>({
		group: { value: props.isEdit ? "" : searchparams.get('group') ?? "", label: props.isEdit ? "" : searchparams.get('name') ?? "" },
		name: "",
		description: "",
		color: defaultColor
	});

	const [error, setError] = React.useState({
		group: false,
		name: false,
		description: false,
		color: false
	})

	const updateState = (key: string, value: string) => {
		setState({ ...state, [key]: value });
		setError({ ...error, [key]: !value?.length })
	}

	const updateDropDown = (key: string, data: SelectBoxOption) => {
		setState({ ...state, [key]: data });
		setError({ ...error, [key]: !data?.value })
	}

	const onClose = (newTag?: SelectBoxOption | ReactSelectOption) => {
		if (props.onDialogClose) {
			props.onDialogClose(newTag);
		} else {
			dispatch(closeDialog());
		}
	}

	const onCreateUpdateBtnClicked = () => {
		if (state.group.value && state.name.length && state.description.length && state.color?.length) {
			if (props.isEdit) {
				updateTag({
					id: props.data._id,
					payload: { tag_name: state.name, tag_desc: state.description, tag_group_id: state.group.value, tag_color: state.color }
				})
			} else {
				createTag({ tag_name: state.name, tag_desc: state.description, tag_group_id: state.group.value, tag_color: state.color, tag_position: props.totalCount ? props.totalCount + 1 : 1 });
			}
		} else {
			setError({
				...error,
				name: !state.name.length, description: !state.description.length, group: !state.group.value, color: !state.color?.length
			})
		}
	}

	const giveMeButtonName = () => {
		return `${props.isEdit ? "Update Tag" : "Create Tag"}`;
	}

	const giveMeInfoText = () => {
		if (props.isEdit && updateTagData.isError) {
			return <Alert severity="error">Oops! Something went wrong, Unable to update Tag. Try Again Later!</Alert>
		}
		if (createTagData.isError) {
			return <Alert severity="error">Oops! Something went wrong, Unable to create Tag. Try Again Later!</Alert>
		}
	};

	React.useEffect(() => {
		if (props?.selectedTagGroup && !props.isEdit) {
			updateDropDown("group", props.selectedTagGroup || null);
		}
		// eslint-disable-next-line
	}, []);

	React.useEffect(() => {
		if (createTagData.isSuccess) {
			enqueueSnackbar("Tag Created Successfully", { variant: "success" });
			props.refetch();
			onClose({ ...createTagData?.data, value: createTagData?.data?._id, label: state.name, tag_name: state.name, tag_color: state.color, tag_desc: state.description, tag_group_id: state.group?.value });
		}
		if (createTagData.isError) {
			enqueueSnackbar("Oops! Something went wrong, Unable to create Tag", { variant: "error" })
		}
		// eslint-disable-next-line
	}, [createTagData]);

	React.useEffect(() => {
		if (props.isEdit) {
			if (updateTagData.isSuccess) {
				enqueueSnackbar("Tag Updated Successfully", { variant: "success" });
				props.refetch();
				onClose({ ...updateTagData?.data, value: updateTagData?.data?._id, label: state.name, tag_name: state.name, tag_color: state.color, tag_desc: state.description, tag_group_id: state.group?.value });
			}
			if (updateTagData.isError) {
				enqueueSnackbar("Oops! Something went wrong, Unable to update Tag", { variant: "error" })
			}
		}
		// eslint-disable-next-line
	}, [updateTagData]);

	React.useEffect(() => {
		if (props.isEdit && props.data?._id && !isFetching && isSuccess) {
			setState({
				...state,
				name: props.data.tag_name,
				description: props.data.tag_desc,
				color: props.data.tag_color ?? defaultColor,
				group: groups?.filter((_: any) => _.value === props.data.tag_group_id)?.[0]
			});

			dispatch(closeBackdrop());
		}
		else if (isFetching) {
			dispatch(openBackdrop("Fetching Tag Categories..."));
		} else if (isSuccess || isError) {
			dispatch(closeBackdrop());
		}
		// eslint-disable-next-line
	}, [props.isEdit, isFetching]);

	return (
		(<Box>
			{/* Fields */}
			<Grid container spacing={2} sx={{
				mt: 1
			}}>
				{/* UoM */}
				<Grid
					size={{
						xs: 12,
						sm: 6,
						md: 6,
						lg: 6,
						xl: 6
					}}>
					<HighlightSelectBox
						margin="none"
						size="medium"
						label='Tag Category'
						disabled={false}
						value={state.group}
						options={groups as SelectBoxOption[]}
						onChange={(data: SelectBoxOption) => updateDropDown("group", data)}
						required={true}
						loading={isFetching}
						error={error.group}
						helperText={error.group ? "Please select Tag Category" : false ? "Oops! Unable to load Tag Categories" : ""}
					/>
				</Grid>

				{/* Name */}
				<Grid
					size={{
						xs: 12,
						sm: 6,
						md: 6,
						lg: 6,
						xl: 6
					}}>
					<TextField
						fullWidth
						label={"Tag Name"}
						value={state.name}
						required={true}
						disabled={false}
						error={error.name}
						helperText={error.name ? "Please enter Tag name" : ""}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateState("name", e.target.value)}
					/>
				</Grid>

				{/* Description */}
				<Grid
					size={{
						xs: 12,
						sm: 12,
						md: 12,
						lg: 12,
						xl: 12
					}}>
					<TextField
						fullWidth
						multiline
						rows={3}
						label={"Tag Description"}
						value={state.description}
						required={true}
						disabled={false}
						error={error.description}
						helperText={error.description ? "Please enter Tag description." : ""}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateState("description", e.target.value)}
					/>
				</Grid>

				{/* Color Palette */}
				<Grid
					size={{
						xs: 12,
						sm: 12,
						md: 12,
						lg: 12,
						xl: 12
					}}>
					<ColorPalette
						label={"Tag Color"}
						value={state.color}
						required={true}
						error={error.color}
						helperText={error.color ? "Please enter Tag Color." : ""}
						onChange={(color: string) => updateState("color", color)}
					/>

					<Box>
						<Typography gutterBottom variant="body2">Preview</Typography>
						<Chip sx={{ borderColor: state.color, backgroundColor: state.color, color: `${hexToGrayscale(state.color) > 150 ? "rgba(0, 0, 0, 0.87)" : "#FFFFFF"}`, width: "fit-content" }} variant="outlined" label={state.name ?? "Tag"} />
					</Box>
				</Grid>

				{/* Info Tex */}
				<Grid size={12}>
					{giveMeInfoText()}
				</Grid>

				{/* Add Noun Btn */}
				<Grid
					size={{
						xs: 12,
						sm: 6,
						md: 6,
						lg: 6,
						xl: 6
					}}>
					<LoadingButton
						fullWidth
						variant="contained"
						onClick={onCreateUpdateBtnClicked}
						loading={createTagData.isLoading || updateTagData.isLoading}
					>
						{giveMeButtonName()}
					</LoadingButton>
				</Grid>

				{/* Cancel Btn */}
				<Grid
					size={{
						xs: 12,
						sm: 6,
						md: 6,
						lg: 6,
						xl: 6
					}}>
					<Button
						fullWidth
						variant="outlined"
						onClick={() => onClose()}
						disabled={false}
					>
						{"Cancel"}
					</Button>
				</Grid>
			</Grid>
		</Box>)
	);
}